import { Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';
import { BasicButton } from '@tassat/button';
import { SimpleErrorMessage, TextField } from '@tassat/formik-input';
import { AddressBookRecord as FredAddressBookRecord } from '@tassat/fred-definitions';
import { Favorite as FavoriteIcon } from '@tassat/icon';
import { AddressBookRecord as PaymentsAddressBookRecord } from '@tassat/payments-definitions';
import { muiTheme } from '@tassat/payments-themes';
import { ButtonType, WalletAddress } from '@tassat/text';
import { ActionTypes } from '@tassat/ui-definitions';
import { Formik, FormikProps } from 'formik';
import * as React from 'react';

import { validateAddCounterpartyForm } from './submission';
import { IFormProps, IFormValues, IFormikProps } from './types';

const FormContents = (props: IFormProps) => {
    const {
        classes,
        features,
        firm,
        values,
        errors,
        submitForm,
        isSubmitting,
        setFieldValue,
        submitError,
        isValid,
        dirty,
        firmWalletInfo,
        setFirmWalletInfo,
        actionType
    } = props;

    const theme = useTheme<typeof muiTheme>();

    const handleWalletAddressInputChange = (e) => {
        if (firmWalletInfo.walletInfo) {
            setFirmWalletInfo({
                walletInfo: false,
                BankName: '',
                FirmName: '',
                WalletName1: '',
                WalletName2: '',
                AssociatedAccount: ''
            });
        }

        const currentValue = e.target.value;
        if (currentValue[0] === 'S') {
            e.target.value = `0x${currentValue.slice(1)}`;
        } else if (currentValue.slice(0, 2) !== '0x' && currentValue.length === 40) {
            e.target.value = `0x${currentValue}`;
        } else if (currentValue.slice(0, 2) !== '0x' && currentValue.length === 2) {
            e.target.value = `0x${currentValue}`;
        } else if (currentValue.slice(0, 2) !== '0x' && currentValue.length >= 2) {
            return;
        }
        setFieldValue('Wallet', e.target.value);
    };

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='column'
                spacing={0}
            >
                <Grid
                    className={classes.fullWidth}
                    container
                    item
                    justifyContent='space-between'
                    xs={9}
                >
                    <Grid
                        item
                        xs={12}
                    >
                        {actionType === ActionTypes.add ? (
                            <TextField
                                customOnChange={(e) => handleWalletAddressInputChange(e)}
                                errors={errors}
                                label='Counterparty Account Address'
                                name='Wallet'
                                placeholder='Account Address'
                                values={values}
                            />
                        ) : (
                            <div className={classes.walletAddressContainer}>
                                <label className={`${classes.inputLabel} ${classes.walletAddressLabel}`}>
                                    Counterparty Account Address
                                </label>
                                <WalletAddress
                                    buttonType={ButtonType.SMALL}
                                    className={classes.walletAddressText}
                                    isAdmin={false}
                                    walletAddress={values.Wallet}
                                />
                            </div>
                        )}
                    </Grid>
                </Grid>
                {firmWalletInfo.walletInfo && firmWalletInfo.FirmName && (
                    <>
                        <Grid
                            className={classes.fullWidth}
                            container
                            item
                            justifyContent='space-between'
                            xs={9}
                        >
                            <Grid
                                item
                                xs={features?.ibnEnabled && firm?.IsIBNEnabled ? 6 : 12}
                            >
                                <label className={classes.inputLabel}>Firm Name</label>
                                <div className={classes.firmAndFavorite}>
                                    <Typography
                                        className={classes.firmName}
                                        variant='h2'
                                    >
                                        {firmWalletInfo.FirmName}
                                    </Typography>
                                </div>
                            </Grid>
                            {features?.ibnEnabled && firm?.IsIBNEnabled && (
                                <Grid
                                    item
                                    xs={6}
                                >
                                    <label className={classes.inputLabel}>Bank Name</label>
                                    <div className={classes.firmAndFavorite}>
                                        <Typography
                                            className={classes.firmName}
                                            variant='h2'
                                        >
                                            {firmWalletInfo.BankName}
                                        </Typography>
                                    </div>
                                </Grid>
                            )}
                        </Grid>
                        <Grid
                            className={classes.fullWidth}
                            container
                            direction='row'
                            item
                            xs={9}
                        >
                            <Grid
                                container
                                direction='row'
                                item
                                wrap='nowrap'
                                xs={6}
                            >
                                <Grid item>
                                    <label className={classes.inputLabel}>Account Name</label>
                                </Grid>
                                <Grid item>
                                    <FavoriteIcon
                                        className='aBookFormBtn'
                                        iconColor={values.IsFavorite ?
                                            (theme?.app?.clientTheme.colors.highlightColor1 || '#EDDA26')
                                            : (theme?.app?.applicationColors.enumColors.greyLighter || '#cfcfcf')}
                                        onClick={() => setFieldValue('IsFavorite', !values.IsFavorite)}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                            >
                                <label className={classes.inputLabel}>Nickname</label>
                            </Grid>
                        </Grid>
                        <Grid
                            className={classes.fullWidth}
                            container
                            direction='row'
                            item
                            xs={9}
                        >
                            <Grid
                                item
                                xs={6}
                            >
                                <Typography
                                    className={classes.nickNamesText}
                                    variant='h2'
                                >
                                    {firmWalletInfo.AccountName}
                                </Typography>
                            </Grid>
                            <Grid
                                item
                                xs={6}
                            >
                                <TextField
                                    errors={errors}
                                    maxLength={64}
                                    name='nickName1'
                                    placeholder='Nickname'
                                    values={values}
                                />
                            </Grid>
                        </Grid>
                        <Grid
                            className={classes.fullWidth}
                            container
                            direction='row'
                            item
                            xs={9}
                        >
                            <Grid
                                item
                                style={{ width: '100%' }}
                                xs={6}
                            />
                            <Grid
                                container
                                direction='column'
                                item
                                spacing={1}
                                xs={6}
                            >
                                <Grid item>
                                    <label className={classes.associatedAccountNumberLabel}>Associated Account Number</label>
                                </Grid>
                                <Grid
                                    className={classes.associatedAccountNumber}
                                    item
                                >
                                    <TextField
                                        errors={errors}
                                        maxLength={40}
                                        name='AssociatedAccountNumber'
                                        placeholder='Associated Account Number'
                                        values={values}
                                    />
                                </Grid>

                            </Grid>
                        </Grid>
                    </>
                )}
                <Grid
                    container
                    direction='column'
                    item
                    justifyContent='center'
                    xs={12}
                >
                    <BasicButton
                        className={
                            `${classes.submitButton} ${!firmWalletInfo.walletInfo &&
                            !firmWalletInfo.FirmName &&
                            classes.hidden}`
                        }
                        disabled={!dirty ||
                            isSubmitting ||
                            !isValid}
                        onClick={submitForm}
                        text='Submit'
                        type='submit'
                    />
                    <SimpleErrorMessage
                        className={classes.submitError}
                        error={submitError}
                    />
                </Grid>
            </Grid>
        </form>
    );
};

export default function AddCounterpartyForm(props: IFormikProps) {
    const {
        classes,
        onClose,
        formProps
    } = props;

    const {
        actionType,
        firm,
        user,
        addressBookContacts,
        selectedContact,
        contactForEdit,
        features,
        validateWalletExists,
        submitAddCounterpartyForm
    } = formProps;

    const [ submitError, setSubmitError ] = React.useState<string>('');

    const [ firmWalletInfo, setFirmWalletInfo ] = React.useState<{
        walletInfo: boolean;
        BankName?: string;
        FirmName: string;
        WalletName1: string;
        WalletName2?: string;
        AssociatedAccountNumber?: string;
    }>({
        walletInfo: false,
        BankName: features?.ibnEnabled &&
            firm?.IsIBNEnabled &&
            actionType === ActionTypes.edit &&
            selectedContact ?
            (selectedContact as PaymentsAddressBookRecord).UICptyBankName
            : '',
        FirmName: actionType === ActionTypes.edit && selectedContact ?
            (selectedContact as FredAddressBookRecord).FirmName : '',
        WalletName1: actionType === ActionTypes.edit && selectedContact ?
            (selectedContact as FredAddressBookRecord).AccountName : '',
        WalletName2: actionType === ActionTypes.edit && selectedContact ? (selectedContact as PaymentsAddressBookRecord).UICptyWalletName2 : '',
        AssociatedAccountNumber: actionType === ActionTypes.edit && selectedContact ? (selectedContact as FredAddressBookRecord).AssociatedAccountNumber : ''
    });

    React.useEffect(() => {
        if (actionType === ActionTypes.edit) {
            setFirmWalletInfo({
                walletInfo: true,
                BankName: '',
                FirmName: (selectedContact as FredAddressBookRecord).FirmName,
                WalletName1: (selectedContact as FredAddressBookRecord).AccountName,
                WalletName2: '',
                AssociatedAccountNumber: (selectedContact as FredAddressBookRecord).AssociatedAccountNumber
            });
        }
    }, []);

    let initialValues = {} as IFormValues;
    if (actionType === ActionTypes.add) {
        initialValues = {
            nickName1: '',
            nickName2: '',
            Wallet: '',
            IsFavorite: false,
            AssociatedAccountNumber: ''
        };
    } else {
        if (!selectedContact) return <div />;
        const {
            IsFavorite,
            NickName,
            Account,
            AssociatedAccountNumber
        } = selectedContact as FredAddressBookRecord;
        initialValues = {
            nickName1: NickName,
            nickName2: '',
            Wallet: Account,
            IsFavorite,
            AssociatedAccountNumber
        };
    }

    const addCounterpartyOriginalValidations = async (values: IFormValues) => {
        const error = validateAddCounterpartyForm(
            values,
            {
                actionType,
                addressBookContacts,
                setFirmWalletInfo,
                initialValues,
                validateWalletExists
            }
        );

        return error;
    };

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                item
                xs={12}
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values: IFormValues) => {
                        try {
                            await submitAddCounterpartyForm(values, {
                                actionType,
                                user,
                                contactForEdit,
                                setSubmitError,
                                onClose
                            });
                        } catch (error) {
                            setSubmitError('Something went wrong');
                        }
                    }}
                    validate={(values: IFormValues) => (addCounterpartyOriginalValidations(values))}
                    validateOnChange
                    validateOnMount
                >
                    {(formContentsProps: FormikProps<IFormValues>) => (
                        <FormContents
                            {...formContentsProps}
                            actionType={actionType}
                            classes={classes}
                            features={features}
                            firm={firm}
                            firmWalletInfo={firmWalletInfo}
                            formProps={formProps}
                            setFirmWalletInfo={setFirmWalletInfo}
                            submitError={submitError}
                        />
                    )}
                </Formik>
            </Grid>
        </Grid>
    );
}